import React, { useState } from 'react';
import { 
  XMarkIcon, 
  EnvelopeIcon, 
  PhoneIcon, 
  AcademicCapIcon, 
  BriefcaseIcon 
} from '@heroicons/react/24/outline';

const Team = () => {
  const [selectedMember, setSelectedMember] = useState(null);

  const directors = [
    {
      name: "Kingsley Mikwamba",
      role: "Director (Rural Development and Extension)",
      edu: "MSc in Rural Development and Extension (Bunda College)",
      email: "kmikwamba@gmail.com",
      emailWork: "kmikwamba@mekconsultantsmw.com",
      phone: "(+265) 995 028 207",
      bio: "A specialist with over 20 years of experience in research and experiential learning-based programs.",
      fullProfile: "Kingsley Mikwamba is a rural development and agricultural extension specialist. He has led various consultancy assignments across Malawi, specializing in adult-based experiential learning and andragogic approaches. His expertise has been utilized in projects funded by the World Bank, USAID, and GIZ.",
      image: "/kingsley.jpg",
      objectPosition: "center center"
    },
    {
      name: "Dr. Evance Chaima",
      role: "Director (Irrigation Agronomy)",
      edu: "PhD in Civil Engineering (Hydraulics), M.Tech in Agricultural Engineering",
      email: "evachaima@gmail.com",
      emailWork: "echaima@mekconsultantsmw.com",
      phone: "+265 886 30 07 38",
      bio: "Seasoned Irrigation Engineer with over 15 years of experience in system design, hydrology, and dam construction.",
      fullProfile: "Dr. Evance Chaima is a registered Graduate Engineer with the Malawi Engineering Institution. His 15+ years of experience covers dam construction, hydrology, and renewable energy. He recently completed his PhD research at Tianjin University, focusing on Civil Engineering and Hydraulics.",
      image: "/evance.jpg",
      objectPosition: "center top"
    },
    {
      name: "Mwabuke Nkhata",
      role: "Director (Data Management)",
      edu: "MSc in Irrigation Engineering (LUANAR)",
      email: "nkhatamwabuke87@gmail.com",
      emailWork: "mnkhata@mekconsultantsmw.com",
      phone: "+265 995 70 78 13",
      bio: "Irrigation Engineer and Agronomist with 14 years of experience, currently a lecturer at LUANAR.",
      fullProfile: "Mwabuke Nkhata has 14 years of experience implementing agricultural and rural development projects for the Government of Malawi and various NGOs. He currently serves as a lecturer in Irrigation Technology at LUANAR (Natural Resources College).",
      image: "/mwabuke.jpg",
      objectPosition: "center center"
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      {/* --- Section Header --- */}
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4 uppercase tracking-tight">Our Resource Personnel</h2>
        <p className="text-gray-600 max-w-2xl mx-auto italic">
          MEK assignments are team facilitated, offering varied and complementary angles to our services.
        </p>
      </div>

      {/* --- Grid --- */}
      <div className="grid md:grid-cols-3 gap-10">
        {directors.map((member, index) => (
          <div key={index} className="flex flex-col items-center text-center bg-white p-8 rounded-2xl shadow-sm border border-gray-100 group transition-all duration-300 hover:shadow-md">
            
            {/* Circular Image Container */}
            <div className="w-48 h-48 rounded-full overflow-hidden mb-6 border-4 border-green-100 shadow-inner flex-shrink-0">
              <img 
                src={member.image} 
                alt={member.name} 
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                style={{ objectPosition: member.objectPosition }}
              />
            </div>
            
            {/* Info */}
            <h3 className="text-xl font-bold text-blue-900 mb-1">{member.name}</h3>
            <p className="text-green-600 font-bold text-xs mb-3 uppercase tracking-wider">{member.role}</p>
            <p className="text-[10px] font-extrabold text-gray-400 mb-4 italic leading-tight">{member.edu}</p>
            
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              {member.bio}
            </p>

            {/* Read More Trigger */}
            <button 
              onClick={() => setSelectedMember(member)}
              className="text-[#1e3a8a] font-bold text-xs uppercase border-b-2 border-transparent hover:border-green-500 transition-all pb-1"
            >
              View Full Profile
            </button>
          </div>
        ))}
      </div>

      {/* --- MODAL POP-UP --- */}
      {selectedMember && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-blue-950/80 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative p-8 md:p-12">
            
            {/* Close Button */}
            <button 
              onClick={() => setSelectedMember(null)}
              className="absolute top-6 right-6 text-gray-400 hover:text-red-500 transition-colors"
            >
              <XMarkIcon className="w-8 h-8" />
            </button>

            {/* Modal Content */}
            <div className="flex flex-col items-center md:items-start text-center md:text-left">
              <h2 className="text-3xl font-black text-blue-950 mb-2">{selectedMember.name}</h2>
              <p className="text-green-600 font-bold text-sm uppercase tracking-widest mb-8">{selectedMember.role}</p>

              {/* Contact Info */}
              <div className="flex flex-wrap gap-6 mb-10 justify-center md:justify-start w-full">
                {/* Emails stacked */}
                <div className="flex items-start gap-2 text-gray-600">
                  <EnvelopeIcon className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                  <div className="flex flex-col gap-1">
                    <a
                      href={`mailto:${selectedMember.email}`}
                      className="text-sm font-semibold hover:text-green-700 transition-colors"
                    >
                      {selectedMember.email}
                    </a>
                    <a
                      href={`mailto:${selectedMember.emailWork}`}
                      className="text-sm font-semibold text-green-700 hover:text-green-800 transition-colors"
                    >
                      {selectedMember.emailWork}
                    </a>
                  </div>
                </div>
                {/* Phone */}
                <div className="flex items-center gap-2 text-gray-600">
                  <PhoneIcon className="w-5 h-5 text-green-500 shrink-0" />
                  <a
                    href={`tel:${selectedMember.phone.replace(/\s/g, '')}`}
                    className="text-sm font-semibold hover:text-green-700 transition-colors"
                  >
                    {selectedMember.phone}
                  </a>
                </div>
              </div>

              {/* Detailed Sections */}
              <div className="space-y-8 w-full">
                <div>
                  <h4 className="flex items-center gap-2 text-blue-950 font-bold uppercase text-xs mb-3">
                    <AcademicCapIcon className="w-4 h-4 text-green-500" />
                    Education
                  </h4>
                  <p className="text-gray-700 text-sm font-medium pl-6 border-l-2 border-green-500">{selectedMember.edu}</p>
                </div>

                <div>
                  <h4 className="flex items-center gap-2 text-blue-950 font-bold uppercase text-xs mb-3">
                    <BriefcaseIcon className="w-4 h-4 text-green-500" />
                    Professional Profile
                  </h4>
                  <div className="bg-gray-50 p-6 rounded-2xl">
                    <p className="text-gray-600 text-sm leading-relaxed italic">
                      "{selectedMember.fullProfile}"
                    </p>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => setSelectedMember(null)}
                className="mt-10 w-full md:w-auto px-10 py-3 bg-blue-950 text-white rounded-xl font-bold hover:bg-green-600 transition-colors shadow-lg"
              >
                Close Profile
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Team;